import { ChangeEvent, useMemo, useState } from "react";
import { validateEmail } from "../../utils/validateEmail";
import "../../ui/elements/input.scss";

type InputEmailProps = {
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  label?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

export const InputEmail = ({
  value,
  onChange,
  label = "Email",
  name = "email",
  id,
  placeholder = "example@mail.com",
  required = false,
  disabled = false,
}: InputEmailProps) => {
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(!required);
  const inputId = useMemo(() => id ?? `input-${name}`, [id, name]);
  const shouldShowError = isTouched && !isValid;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    let nextIsValid = isValid;

    // While typing, re-check only to clear previous invalid state.
    if (isTouched && !isValid) {
      const candidateIsValid = nextValue.length > 0 ? validateEmail(nextValue) : !required;
      if (candidateIsValid) {
        setIsValid(true);
        nextIsValid = true;
      }
    }

    onChange(nextValue, nextIsValid);
  };

  const handleBlur = () => {
    setIsTouched(true);
    const nextIsValid = value.length > 0 ? validateEmail(value) : !required;
    setIsValid(nextIsValid);
    onChange(value, nextIsValid);
  };

  return (
    <div className="input">
      <label className="input__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        className={`input__field${shouldShowError ? " input__field--error" : ""}`}
        id={inputId}
        name={name}
        type="email"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="email"
        required={required}
        disabled={disabled}
        aria-invalid={shouldShowError}
      />
      {shouldShowError ? <span className="input__error">Введите корректный email</span> : null}
    </div>
  );
};
